import { DatePipe, formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { map, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { IOption } from 'src/app/components/shared/form/components/form-select/form-select.component';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';
import {
  CODOutstandingReport,
  CODOutstandingReportRequest,
  CODOutstandingReportResponse,
} from 'src/app/core/models/reports/cod-outstanding-report.model';
import { CODCollectedReportResponse } from 'src/app/core/models/reports/reports.model';
import { CodsReportsService } from 'src/app/core/services/reports/cod/cod-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import { PDF_REPORT } from '../collected/collected.constants';

import { PdfExportService } from 'src/app/core/services/reports/pdf-export.service';
import { OUTSTANDING_PDF_REPORT } from '../outstanding/outstanding.constant';
import {
  CONSTS,
  DOWNLOAD_FILE_NAMES,
  GroupCodExtractFormGroup,
  REPORT_OPTIONS,
  Reports,
} from './group-cod-extract.constants';

@Component({
  selector: 'app-group-cod-extract',
  templateUrl: './group-cod-extract.component.html',
  styleUrls: ['./group-cod-extract.component.scss'],
  standalone: false,
})
export class GroupCodExtractComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: GroupCodExtractFormGroup = inject(MAT_DIALOG_DATA);
  readonly datePipe = inject(DatePipe);
  private readonly userService = inject(UserService);
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly codReportsService: CodsReportsService =
    inject(CodsReportsService);
  private readonly reportUtils: ReportsUtils = inject(ReportsUtils);
  private readonly commonUtils: CommonUtils = inject(CommonUtils);
  private readonly pdfExportService: PdfExportService =
    inject(PdfExportService);

  readonly CONSTS = CONSTS;
  readonly SRC = IMAGE_SRC;
  readonly BUTTON_TYPES = BUTTON_TYPES;
  readonly PDF_REPORT = PDF_REPORT;
  readonly OUTSTANDING_PDF_REPORT = OUTSTANDING_PDF_REPORT;
  readonly REPORT_OPTIONS = REPORT_OPTIONS;

  @ViewChild('outstandingPdfContainer', { read: ViewContainerRef })
  outstandingPdfContainer!: ViewContainerRef;

  @ViewChild('collectedPdfContainer', { read: ViewContainerRef })
  collectedPdfContainer!: ViewContainerRef;

  extractForm!: FormGroup;
  routes: IOption[] = [];
  codCollectedResponse!: CODCollectedReportResponse;
  codOutstandingResponse!: CODOutstandingReportResponse;
  groupCode!: string;
  hasSubmitted = false;
  selectableDays = 60;

  currDate!: Date;
  min60Days!: Date;
  min1Day!: Date;

  ngOnInit() {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((user) => {
          this.setDates();

          this.extractForm = this.fb.group({
            fromDate: [this.min1Day],
            toDate: [this.min1Day],
            reportFormat: [Options.PDF],
            report: [REPORT_OPTIONS[0].value],
          });

          this.extractForm.get('reportFormat')?.disable();

          this.groupCode = user.locationGroup;
        }),
      )
      .subscribe();
  }

  getReport() {
    this.hasSubmitted = true;
    switch (this.extractForm.get('report')?.value) {
      case Reports.COD_RECONCILE_REPORT:
        this.getCodReconcileReport();
        break;
      case Reports.COD_COLLECTED_CLEARED:
        this.getCodCollectedReport();
        break;
      case Reports.COD_OUTSTANDING:
        this.getCodOutstandingReport();
        break;
    }
  }

  clickExit() {
    this.commonUtils.openExitModal(
      !this.hasSubmitted && this.extractForm.dirty,
    );
  }

  radioChange(newValue: Options) {
    this.extractForm.get('reportFormat')?.setValue(newValue);
  }

  updateReports() {
    switch (this.extractForm.get('report')?.value) {
      case Reports.COD_RECONCILE_REPORT:
        this.extractForm.get('reportFormat')?.disable();
        break;
      case Reports.COD_COLLECTED_CLEARED:
      case Reports.COD_OUTSTANDING:
        this.extractForm.get('reportFormat')?.enable();
        this.extractForm.get('reportFormat')?.enable();
        break;
    }

    this.extractForm.get('fromDate')?.setValue(this.min1Day);
    this.extractForm.get('toDate')?.setValue(this.min1Day);
  }

  private getCodReconcileReport() {
    const formValue = this.extractForm.value;

    const fromDate = this.commonUtils.getFormattedDate(formValue.fromDate);
    const toDate = this.commonUtils.getFormattedDate(formValue.toDate);

    const request = {
      fromDate: fromDate,
      toDate: toDate,
      route: 'All',
      groupCode: this.groupCode,
    };

    this.codReportsService
      .getCODReconcileReport(request)
      .pipe(
        take(1),
        map((data) => {
          const codReconcileReport = data.response.CODReconcile;

          if (codReconcileReport?.length) {
            const customHeader = [
              ['', '', '', '', `${'COD Reconcile Report'}`, '', '', '', '', ''],
            ];
            this.reportUtils.exportCSVReport(
              codReconcileReport,
              DOWNLOAD_FILE_NAMES[Reports.COD_RECONCILE_REPORT],
              customHeader,
            );
          } else {
            this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
          }
        }),
      )
      .subscribe();
  }

  private getCodCollectedReport(): void {
    const formValue = this.extractForm.value;

    const fromDate = this.commonUtils.getFormattedDate(formValue.fromDate);
    const toDate = this.commonUtils.getFormattedDate(formValue.toDate);
    const formatType = this.extractForm.get('reportFormat')?.value;

    const request = {
      fromdate: fromDate,
      todate: toDate,
      groupCode: this.groupCode,
    };

    this.codReportsService
      .getCODCollectedReport(request)
      .pipe(take(1))
      .subscribe({
        next: async (data) => {
          const records = data?.response?.CODCollectedCleared ?? [];

          if (!records.length) {
            this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
            return;
          }

          // ---------- CSV ----------
          if (formatType === Options.CSV) {
            this.exportCodCollectedCsv(data);
            return;
          }

          // ---------- PDF ----------
          if (formatType === Options.PDF) {
            await this.pdfExportService.exportGroupCodClearedPdfAutoTable(
              data,
              'COD Collected / Cleared',
              DOWNLOAD_FILE_NAMES[Reports.COD_COLLECTED_CLEARED],
              data.response.turninDate,
            );
          }
        },
        error: () => {
          this.commonUtils.updateBannerType(BANNER_TYPE.API_400_ERROR);
        },
      });
  }

  private getCodOutstandingReport() {
    const formValue = this.extractForm.value;
    const fromdate = this.commonUtils.getFormattedDate(formValue.fromDate);
    const todate = this.commonUtils.getFormattedDate(formValue.toDate);

    const request: CODOutstandingReportRequest = {
      fromdate,
      todate,
      groupCode: this.groupCode,
    };

    this.codReportsService
      .getCODOutstandingReport(request)
      .pipe(
        take(1),
        map((data: CODOutstandingReportResponse) => {
          const formatType = this.extractForm.get('reportFormat')?.value;
          const CODOutstanding = data.response.CODCollectedCleared;
          if (CODOutstanding?.length) {
            if (formatType === Options.CSV) {
              const customHeader = [
                ['', '', '', '', `${'COD Outstanding'}`, '', '', '', '', ''],
                [
                  '',
                  '',
                  'Location:',
                  '',
                  `${data.response.location}`,
                  '',
                  '',
                  'Turn in ',
                  '',
                  `${data.response.turninDate}`,
                ],
              ];
              const formatedDate = formatDate(
                new Date(),
                'MM-dd-yyyy',
                'en-US',
              );

              const CSV_FILE_COLUMN_HEADING = [
                'Driver'.toUpperCase(),
                'Loop'.toUpperCase(),
                'Trackno'.toUpperCase(),
                'Customer'.toUpperCase(),
                'City'.toUpperCase(),
                'Expected'.toUpperCase(),
                'CA/CH/C',
                'D/P',
                'TurnedIn'.toUpperCase(),
                'Comments'.toUpperCase(),
                'Collected/Cleared Date'.toUpperCase(),
                'Delivery Date'.toUpperCase(),
                'LocationCode'.toUpperCase(),
              ];
              const projectedRows = CODOutstanding.map(
                (data: CODOutstandingReport) => ({
                  Driver: data.driver ?? '',
                  Loop: data.loop ?? '',
                  Trackno: data.trackno ?? '',
                  Customer: data.customer ?? '',
                  City: data.city ?? '',
                  Expected: data.expected ?? '',
                  'CA/CH/C': data['ca/ch/cc'] ?? '',
                  'D/P': data['d/p'] ?? '',
                  TurnedIn: data.turnedin ?? '',
                  Comments: data.comments ?? '',
                  'Collected/Cleared Date': data.codturnindate ?? '',
                  'Delivery Date': data.date ?? '',
                  LocationCode: data.locationcode ?? '',
                }),
              );

              const customFooter = [
                [],
                [
                  'Total Expected ',
                  '',
                  `${data?.response.TotalExpectedCOD}`,
                  '',
                  '',
                  '',
                  'Total Turned In',
                  '',
                  `${data?.response.TotalTurnedInCOD}`,
                  '',
                  'Turn In',
                  `${data?.response.turninDate}`,
                ],
                ['Printed:', `${formatedDate}`],
              ];
              this.reportUtils.exportCSVReport(
                projectedRows,
                DOWNLOAD_FILE_NAMES[Reports.COD_OUTSTANDING],
                customHeader,
                customFooter,
                CSV_FILE_COLUMN_HEADING,
              );
            }
            if (formatType === Options.PDF) {
              this.codOutstandingResponse = data;
              setTimeout(() => {
                const content =
                  this.outstandingPdfContainer.element.nativeElement.innerHTML;
                this.reportUtils.exportPdfReport(
                  content,
                  OUTSTANDING_PDF_REPORT.NAME,
                  DOWNLOAD_FILE_NAMES[Reports.COD_OUTSTANDING],
                );
              }, 0);
            }
          } else {
            this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
          }
        }),
      )
      .subscribe();
  }

  private setDates() {
    this.currDate = new Date();

    this.min60Days = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 60,
    );

    this.min1Day = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 1,
    );
  }

  private exportCodCollectedCsv(data: any): void {
    const formatedDate = formatDate(new Date(), 'MM-dd-yyyy', 'en-US');

    const CSV_FILE_COLUMN_HEADING = [
      'DRIVER',
      'LOOP',
      'TRACKNO',
      'CUSTOMER',
      'CITY',
      'EXPECTED',
      'CA/CH/C',
      'D/P',
      'TURNEDIN',
      'COMMENTS',
      'COLLECTED/CLEARED DATE',
      'DELIVERY DATE',
      'LOCATIONCODE',
    ];

    const projectedRows = data.response.CODCollectedCleared.map(
      (r: {
        [x: string]: any;
        driver: any;
        loop: any;
        trackno: any;
        customer: any;
        city: any;
        expected: any;
        turnedin: any;
        comments: any;
        codturnindate: any;
        date: any;
        locationcode: any;
      }) => ({
        Driver: r.driver ?? '',
        Loop: r.loop ?? '',
        Trackno: r.trackno ?? '',
        Customer: r.customer ?? '',
        City: r.city ?? '',
        Expected: r.expected ?? '',
        'CA/CH/C': r['ca/ch/cc'] ?? '',
        'D/P': r['d/p'] ?? '',
        TurnedIn: r.turnedin ?? '',
        Comments: r.comments ?? '',
        'Collected/Cleared Date': r.codturnindate ?? '',
        'Delivery Date': r.date ?? '',
        LocationCode: r.locationcode ?? '',
      }),
    );

    const customHeader = [
      ['', '', '', '', 'COD Collected/Cleared', '', '', '', '', ''],
      [
        '',
        '',
        'Location:',
        '',
        data.response.location,
        '',
        '',
        'Turn In',
        '',
        data.response.turninDate,
      ],
    ];

    const customFooter = [
      [],
      [
        'Total Expected',
        '',
        data.response.TotalExpectedCOD,
        '',
        '',
        '',
        'Total Turned In',
        '',
        data.response.TotalTurnedInCOD,
        '',
        'Turn In',
        data.response.turninDate,
      ],
      ['Printed:', formatedDate],
    ];

    this.reportUtils.exportCSVReport(
      projectedRows,
      DOWNLOAD_FILE_NAMES[Reports.COD_COLLECTED_CLEARED],
      customHeader,
      customFooter,
      CSV_FILE_COLUMN_HEADING,
    );
  }
}
